import { useEffect, useMemo, useState } from "react";

import {
  TextArea,
  TextField,
  Select,
  Modal,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";
import { Row, Col, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { hasAllowedRoleForEditing } from "./CommitteeDecision";
import { DEFAULT_COMMITTEE_DECISION, DEFAULT_REQUEST } from "./constants";
import { formatDateTimeWithUser, shouldShowWarning } from "./utils";
import {
  deleteCommitteeDecision,
  getCommitteeDecision,
  saveCommitteeDecision,
} from "../../api/Request/RequestApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  RequestHeader,
} from "../../components";
import { useAuth } from "../../hooks";
import { ValidationFailure } from "../../models";
import { CommitteeTypeEnum } from "../../models/Committee";
import {
  Request,
  CommitteeLanguageDecision,
  CommitteeEnrollmentDecision,
  CommitteeDecision,
} from "../../models/Request";
import {
  committeeDecisionDetailsUrl,
  requestAdminDetailsUrl,
} from "../../routes/routes";
import {
  resetIsEdited,
  setIsEdited,
  setSaveOK,
} from "../../slices/applicationSlice";
import { RuleUtils, FormatUtils, MsgUtils } from "../../utils";

export function CommitteeDecisionEdit() {
  const { committeeDecisionId, requestId, committeeEmployeeId } = useParams<{
    committeeDecisionId: string;
    requestId: string;
    committeeEmployeeId: string;
  }>();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const [item, setItem] = useState<Request>(DEFAULT_REQUEST);
  const [croatianTeacher, setCroatianTeacher] = useState(false);
  const [initialData, setInitialData] = useState<CommitteeDecision>(
    DEFAULT_COMMITTEE_DECISION
  );

  const [committeeDecision, setCommitteeDecision] = useState<CommitteeDecision>(
    DEFAULT_COMMITTEE_DECISION
  );

  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [showOpinionExistsModal, setShowOpinionExistsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    (async () => {
      if (committeeDecisionId && requestId && committeeEmployeeId) {
        const result = await getCommitteeDecision(
          +committeeDecisionId,
          +requestId,
          +committeeEmployeeId
        );

        if (result) {
          setInitialData(result);
          setCommitteeDecision(result);

          const isCountyCommittee =
            result.committeeEmployee?.committeeRole?.committeeType ===
            CommitteeTypeEnum.County;

          setShowOpinionExistsModal(
            isCountyCommittee
              ? !!result.hasDecisionsInPreviousCountyCommittees
              : !!result.hasDecisionsInPreviousInstitutionCommittees
          );

          if (result.request) setItem(result.request);

          setCroatianTeacher(
            RuleUtils.isCroatianTeacherRole(
              result.committeeEmployee?.committeeRole?.id
            )
          );
        }
      }
      dispatch(setIsEdited());
    })();
  }, [committeeDecisionId, requestId, committeeEmployeeId]);

  const handleRemarkChanged = (val?: string) => {
    const d: CommitteeDecision = {
      ...committeeDecision,
      remark: val,
    };
    handleUpdate(d);
  };

  const handleEnrollmentDecisionChanged = (val: number) => {
    if (!isNaN(val)) {
      const d: CommitteeDecision = {
        ...committeeDecision,
        committeeEnrollmentDecisionId: val,
      };
      handleUpdate(d);
    }
  };
  const handleLanguageDecisionChanged = (val: number) => {
    const d: CommitteeDecision = {
      ...committeeDecision,
      committeeLanguageDecisionId: !isNaN(val) ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleUpdate = (item: CommitteeDecision) => {
    setCommitteeDecision(item);
    dispatch(setIsEdited());
  };
  const handleSaveClicked = async () => {
    const result = await saveCommitteeDecision(committeeDecision);
    if (result) {
      setErrors(result.validationFailures);
      if (result.isValid && result.result) {
        dispatch(resetIsEdited());
        dispatch(setSaveOK());
        history.replace({
          pathname: committeeDecisionDetailsUrl?.(
            result.result?.id,
            Number(requestId),
            Number(committeeEmployeeId)
          ),
        });
      }
    }
  };

  const isAllowedToDelete = useMemo(() => {
    const isRoleAllowed = hasAllowedRoleForEditing(
      initialData.committeeEmployee?.committeeRole?.committeeType ||
        CommitteeTypeEnum.Institution,
      auth
    );

    return isRoleAllowed;
  }, [auth, initialData.committeeEmployee?.committeeRole?.committeeType]);

  const handleDeleteClicked = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!committeeDecision.id) return;

    try {
      await deleteCommitteeDecision(committeeDecision.id);
      setShowDeleteModal(false);

      dispatch(resetIsEdited());
      dispatch(setSaveOK());
      history.push(requestAdminDetailsUrl(+requestId));
    } catch (error) {
      setErrors([
        { propertyName: "delete", message: t("committeeDecision.deleteError") },
      ]);
    }
  };

  const hasOpinion = committeeDecision.id !== 0;

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={"hide"}
        pageTitle={t("committeeDecision.committeeDecisionPageTitle")}
        errors={errors}
      />
      <RequestHeader request={item} />
      <FormCard>
        {shouldShowWarning(committeeDecision) && (
          <Typography variant="base" color="danger">
            {t("committeeDecision.outsideEnrollmentAreaMessage")}
          </Typography>
        )}
        <Row>
          <Col md={10}>
            <TextArea
              ariaLabel={t("committeeDecision.remark")}
              label={t("committeeDecision.remark")}
              value={committeeDecision?.remark || ""}
              maxLength={4000}
              onChange={(e) => handleRemarkChanged(e.target.value)}
              rows={10}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("Remark"))
                  ?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <div
              style={{ display: "flex", gap: "0.3rem", alignItems: "flex-end" }}
            >
              <TextField
                label={t("committeeDecision.committeeEmployee")}
                value={`${committeeDecision?.committeeEmployee?.employee?.name}, ${committeeDecision?.committeeEmployee?.committeeRole.name}`}
                plaintext
              />
              {!!committeeDecision?.committeeEmployee?.deactivationDate && (
                <TextField
                  value={`- aktivan do ${FormatUtils.formatDate(
                    committeeDecision?.committeeEmployee?.deactivationDate
                  )}`}
                  plaintext
                  bold
                />
              )}
            </div>
          </Col>
          {committeeDecision?.committeeEmployee?.committeeId !==
            item.institutionCommitteeId && (
            <Col md={6}>
              <TextField
                label={t("committeeDecision.committeePosition")}
                value={
                  committeeDecision?.committeeEmployee?.committeePosition?.name
                }
                plaintext
              />
            </Col>
          )}
        </Row>
        <Row>
          <Col md={4}>
            <Select
              label={t("committeeDecision.committeeEnrollmentDecision")}
              value={committeeDecision.committeeEnrollmentDecisionId}
              onChange={(e) =>
                handleEnrollmentDecisionChanged(Number.parseInt(e.target.value))
              }
              selectData={codeBookToSelectData(
                MsgUtils.getEnumToCodeBook(
                  CommitteeEnrollmentDecision,
                  "committeeEnrollmentDecision"
                )
              )}
              errorText={
                errors?.find((e) =>
                  e.propertyName?.startsWith("CommitteeEnrollmentDecisionId")
                )?.message
              }
            />
          </Col>
        </Row>
        {croatianTeacher && (
          <Row>
            <Col md={6}>
              <Select
                label={t("committeeDecision.committeeLanguageDecision")}
                value={committeeDecision.committeeLanguageDecisionId}
                onChange={(e) =>
                  handleLanguageDecisionChanged(Number.parseInt(e.target.value))
                }
                selectData={codeBookToSelectData(
                  MsgUtils.getEnumToCodeBook(
                    CommitteeLanguageDecision,
                    "committeeLanguageDecision"
                  )
                )}
                emptyOption={{ text: "", value: undefined }}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("CommitteeLanguageDecisionId")
                  )?.message
                }
              />
            </Col>
          </Row>
        )}
        {hasOpinion && (
          <>
            <Row>
              <Col md={12}>
                <TextField
                  label={t("committeeDecision.createdDateTimeUser")}
                  value={formatDateTimeWithUser(
                    committeeDecision?.dateCreated,
                    committeeDecision?.createdAppUser
                  )}
                  plaintext
                />
              </Col>
            </Row>
            {committeeDecision.dateUpdated && (
              <Row>
                <Col md={12}>
                  <TextField
                    label={t("committeeDecision.lastChangedDateTimeUser")}
                    value={formatDateTimeWithUser(
                      committeeDecision?.dateUpdated,
                      committeeDecision?.appUser
                    )}
                    plaintext
                  />
                </Col>
              </Row>
            )}
          </>
        )}
        {isAllowedToDelete && hasOpinion && (
          <Button variant="outline-danger" onClick={handleDeleteClicked}>
            {t("common.delete")}
          </Button>
        )}
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={"show"}
        onClickSave={handleSaveClicked}
      />
      <Modal
        type="message"
        confirmText={t("common.close")}
        open={showOpinionExistsModal}
        onConfirm={() => setShowOpinionExistsModal(false)}
        onClose={() => setShowOpinionExistsModal(false)}
      >
        {t("committeeDecision.opinionExistsMessage")}
      </Modal>
      <Modal
        type="confirm"
        confirmText={t("common.delete")}
        cancelText={t("common.quit")}
        open={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onClose={() => setShowDeleteModal(false)}
      >
        {t("committeeDecision.deleteConfirmation")}
      </Modal>
    </ContentArea>
  );
}
